import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { formatApiError, SPEC_KEYS, statusBadgeClass, STATUS_LABELS, fmtNum, LOCATIONS } from "@/lib/utils";
import { ClipboardCheck, X, Check } from "lucide-react";

const emptySpecs = SPEC_KEYS.reduce((acc, s) => ({ ...acc, [s.key]: "" }), {});
const LOC_TABS = ["ALL", ...LOCATIONS];

export default function QC() {
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [specs, setSpecs] = useState(emptySpecs);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("ALL");

  const load = useCallback(async () => {
    const res = await api.get("/batches", { params: { status: "pending_qc" } });
    setBatches(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibleBatches = useMemo(
    () => batches.filter((b) => tab === "ALL" ? true : (b.location || "FACTORY") === tab),
    [batches, tab]
  );

  const tabCounts = useMemo(() => {
    const c = { ALL: batches.length };
    for (const l of LOCATIONS) c[l] = batches.filter((b) => (b.location || "FACTORY") === l).length;
    return c;
  }, [batches]);

  const submit = async (decision) => {
    if (!selected) return;
    setErr(""); setLoading(true);
    try {
      const numericSpecs = Object.fromEntries(
        Object.entries(specs).map(([k, v]) => [k, v === "" ? null : parseFloat(v)])
      );
      await api.post(`/batches/${selected.id}/qc`, {
        decision,
        specs: numericSpecs,
        qc_notes: notes,
      });
      setSelected(null);
      setSpecs(emptySpecs);
      setNotes("");
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl">
      <PageHeader
        overline="QC MANAGER"
        title="QC Approval Queue"
        description="Enter specifications and approve or reject finished-goods batches before they enter stock. Filter by warehouse."
      />

      <div className="flex gap-1 mb-4 border-b overflow-x-auto" style={{ borderColor: "var(--border)" }}>
        {LOC_TABS.map((t) => (
          <button
            key={t}
            className="px-4 py-2 text-sm transition-colors whitespace-nowrap"
            style={{
              color: tab === t ? "var(--text)" : "var(--text-muted)",
              borderBottom: tab === t ? "2px solid var(--orange)" : "2px solid transparent",
              marginBottom: "-1px",
              fontWeight: tab === t ? 600 : 400,
            }}
            onClick={() => setTab(t)}
            data-testid={`qc-tab-${t.toLowerCase()}`}
          >
            {t} <span className="font-mono text-xs" style={{ color: "var(--text-dim)" }}>({tabCounts[t] || 0})</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Queue */}
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
            <div className="font-heading text-sm font-semibold tracking-tight">PENDING QC @ <span style={{ color: "var(--orange)" }}>{tab}</span> ({visibleBatches.length})</div>
          </div>
          <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {visibleBatches.length === 0 ? (
              <div className="p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>
                No batches pending QC at {tab}. ✓
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Batch</th>
                    <th>Location</th>
                    <th>Date</th>
                    <th>Shift</th>
                    <th className="text-right">Bags</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleBatches.map((b) => (
                    <tr key={b.id} data-testid={`qc-row-${b.batch_number}`}>
                      <td className="font-mono">{b.batch_number}</td>
                      <td className="font-mono text-xs uppercase">{b.location || "FACTORY"}</td>
                      <td className="font-mono">{b.production_date}</td>
                      <td><span className={statusBadgeClass(b.status)}>{b.shift}</span></td>
                      <td className="font-mono text-right">{fmtNum(b.bags_count)}</td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: "4px 10px", fontSize: "12px" }}
                          onClick={() => { setSelected(b); setSpecs(emptySpecs); setNotes(""); setErr(""); }}
                          data-testid={`select-qc-${b.batch_number}`}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Detail */}
        <div className="card p-6">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-sm" style={{ color: "var(--text-muted)", minHeight: "300px" }}>
              ← Select a batch to enter specifications
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-5">
                <div>
                  <div className="text-xs font-mono mb-1" style={{ color: "var(--orange)" }}>// IN-HOUSE LAB ENTRY</div>
                  <div className="font-heading text-xl font-bold tracking-tight font-mono">{selected.batch_number}</div>
                  <div className="text-xs font-mono mt-1" style={{ color: "var(--text-muted)" }}>
                    {selected.production_date} · {selected.shift.toUpperCase()} SHIFT · {selected.bags_count} BAGS · {selected.bags_count * 50} KG · @ {selected.location || "FACTORY"}
                  </div>
                </div>
                <button className="btn btn-ghost" style={{ padding: "4px" }} onClick={() => setSelected(null)} data-testid="close-qc-detail">
                  <X size={16} />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-5">
                {SPEC_KEYS.map((s) => (
                  <div key={s.key}>
                    <label style={{ fontSize: "10px" }}>{s.label}{s.suffix && ` (${s.suffix.trim()})`}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={specs[s.key]}
                      onChange={(e) => setSpecs({ ...specs, [s.key]: e.target.value })}
                      placeholder="—"
                      style={{ fontFamily: "JetBrains Mono, monospace" }}
                      data-testid={`spec-${s.key}-input`}
                    />
                  </div>
                ))}
              </div>

              <label>QC Notes</label>
              <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional remarks..." data-testid="qc-notes-input" />

              {err && (
                <div className="text-xs p-2 border mt-3" style={{ color: "var(--red)", borderColor: "var(--red)", background: "rgba(239,68,68,0.1)" }}>
                  {err}
                </div>
              )}

              <div className="flex gap-3 mt-5">
                <button
                  className="btn btn-primary flex-1"
                  onClick={() => submit("approved")}
                  disabled={loading}
                  data-testid="qc-approve-button"
                >
                  <Check size={16} /> Approve
                </button>
                <button
                  className="btn btn-danger flex-1"
                  onClick={() => submit("rejected")}
                  disabled={loading}
                  data-testid="qc-reject-button"
                >
                  <X size={16} /> Reject
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
